import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Button } from 'primereact/button';
import { CourseHourChangeRequestWithUsers } from '../../../../../api/types/programs/course/course-hours-request-change/course-hours-request-change.type';

interface EditCourseHoursRequestDialogProps {
  visible: boolean;
  onHide: () => void;
  request: CourseHourChangeRequestWithUsers | null;
  onSave: (newHours: number, newJustification: string) => Promise<void>;
}

const EditCourseHoursRequestDialog: React.FC<
  EditCourseHoursRequestDialogProps
> = ({ visible, onHide, request, onSave }) => {
  const [loading, setLoading] = useState(false);
  const [newHours, setNewHours] = useState<number>(0);
  const [newJustification, setNewJustification] = useState<string>('');
  const [errors, setErrors] = useState<{
    hours?: string;
    justification?: string;
  }>({});

  // Reset form when dialog opens/closes or request changes
  useEffect(() => {
    if (visible && request) {
      setNewHours(request.requestedAdditionalHours);
      setNewJustification(request.justification);
      setErrors({});
    } else {
      setNewHours(0);
      setNewJustification('');
      setErrors({});
    }
  }, [visible, request]);

  const validateForm = (): boolean => {
    const newErrors: { hours?: string; justification?: string } = {};

    if (!newHours || newHours <= 0) {
      newErrors.hours = 'שעות חייבות להיות מספר חיובי';
    }

    if (!newJustification || newJustification.trim().length === 0) {
      newErrors.justification = 'הצדקה היא שדה חובה';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      await onSave(newHours, newJustification.trim());
      onHide();
    } catch (error) {
      console.error('Error updating course hours request:', error);
      // Error handling could be improved here with toast notification
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (!loading) {
      onHide();
    }
  };

  const footer = (
    <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
      <Button
        label="ביטול"
        severity="secondary"
        outlined
        onClick={handleCancel}
        disabled={loading}
      />
      <Button
        label="שמור"
        severity="success"
        onClick={handleSave}
        loading={loading}
        disabled={loading}
      />
    </div>
  );

  return (
    <Dialog
      header="ערוך בקשת שינוי שעות"
      visible={visible}
      onHide={handleCancel}
      style={{ width: '500px' }}
      modal
      footer={footer}
    >
      <div
        style={{
          direction: 'rtl',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
        }}
      >
        {request && (
          <div
            style={{
              marginBottom: '16px',
              padding: '12px',
              backgroundColor: '#f8f9fa',
              borderRadius: '4px',
            }}
          >
            <strong>בקשה מקורית:</strong>
            <div>
              מבקש: {request.creatorFirstName} {request.creatorLastName}
            </div>
            <div>
              תאריך: {new Date(request.createdAt).toLocaleDateString('he-IL')}
            </div>
          </div>
        )}

        <div>
          <label
            htmlFor="newHours"
            style={{
              display: 'block',
              marginBottom: '4px',
              fontWeight: 'bold',
            }}
          >
            שעות מבוקשות:
          </label>
          <InputText
            id="newHours"
            type="number"
            value={newHours.toString()}
            onChange={(e) => setNewHours(parseInt(e.target.value) || 0)}
            style={{ width: '100%' }}
            disabled={loading}
            className={errors.hours ? 'p-invalid' : ''}
          />
          {errors.hours && (
            <small style={{ color: 'red', display: 'block', marginTop: '4px' }}>
              {errors.hours}
            </small>
          )}
        </div>

        <div>
          <label
            htmlFor="newJustification"
            style={{
              display: 'block',
              marginBottom: '4px',
              fontWeight: 'bold',
            }}
          >
            הצדקה:
          </label>
          <InputTextarea
            id="newJustification"
            value={newJustification}
            onChange={(e) => setNewJustification(e.target.value)}
            rows={4}
            style={{ width: '100%', resize: 'vertical' }}
            disabled={loading}
            className={errors.justification ? 'p-invalid' : ''}
            placeholder="הכנס הצדקה לבקשת השינוי..."
          />
          {errors.justification && (
            <small style={{ color: 'red', display: 'block', marginTop: '4px' }}>
              {errors.justification}
            </small>
          )}
        </div>
      </div>
    </Dialog>
  );
};

export default EditCourseHoursRequestDialog;
